/**
 * useCommandPalette Hook
 *
 * Global keyboard shortcut handler for Command Palette (Cmd+K / Ctrl+K)
 * Manages open/close state and provides commands registry
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Upload,
  ClipboardCheck,
  BarChart3,
  Tag,
  ListChecks,
  CreditCard,
  Settings,
  Users,
  Wallet,
  Bell,
  HelpCircle,
  BookOpen,
  History,
  PieChart,
  Plus,
  Search,
  type LucideIcon,
} from 'lucide-react';

export interface CommandItem {
  id: string;
  title: string;
  subtitle?: string;
  icon: LucideIcon;
  action: (query?: string) => void;
  keywords: string[];
  category: 'navigation' | 'action' | 'recent';
}

interface UseCommandPaletteReturn {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;
  commands: CommandItem[];
  filterCommands: (query: string) => CommandItem[];
}

export function useCommandPalette(): UseCommandPaletteReturn {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  const open = useCallback(() => setIsOpen(true), []);
  const close = useCallback(() => setIsOpen(false), []);
  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);

  // Register global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd+K (Mac) or Ctrl+K (Windows/Linux)
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        toggle();
      }

      // Escape to close
      if (e.key === 'Escape' && isOpen) {
        e.preventDefault();
        close();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, toggle, close]);

  // Command definitions
  const commands = useMemo<CommandItem[]>(() => {
    const navigateTo = (path: string) => {
      navigate(path);
      close();
    };

    return [
      // Navigation commands
      {
        id: 'nav-dashboard',
        title: '대시보드',
        subtitle: '연간 현황 및 요약',
        icon: LayoutDashboard,
        action: () => navigateTo('/dashboard'),
        keywords: ['dashboard', '대시보드', '홈', 'home', '연간', 'annual'],
        category: 'navigation',
      },
      {
        id: 'nav-reports',
        title: '월간 리포트',
        subtitle: '월별 상세 분석',
        icon: BarChart3,
        action: () => navigateTo('/reports'),
        keywords: ['reports', '리포트', '월간', 'monthly', '분석', 'analysis'],
        category: 'navigation',
      },
      {
        id: 'nav-budget',
        title: '예산 관리',
        subtitle: '예산 설정 및 현황',
        icon: Wallet,
        action: () => navigateTo('/budget'),
        keywords: ['budget', '예산', '지출', 'spending'],
        category: 'navigation',
      },
      {
        id: 'nav-upload',
        title: '파일 업로드',
        subtitle: '거래 내역 가져오기',
        icon: Upload,
        action: () => navigateTo('/upload'),
        keywords: ['upload', '업로드', '가져오기', 'import', 'csv', 'excel'],
        category: 'navigation',
      },
      {
        id: 'nav-review',
        title: '거래 검수',
        subtitle: '분류 확인 및 수정',
        icon: ClipboardCheck,
        action: () => navigateTo('/review'),
        keywords: ['review', '검수', '거래', 'transaction', '분류'],
        category: 'navigation',
      },
      {
        id: 'nav-categories',
        title: '카테고리 관리',
        subtitle: '분류 체계 설정',
        icon: Tag,
        action: () => navigateTo('/categories'),
        keywords: ['categories', '카테고리', '분류', 'category'],
        category: 'navigation',
      },
      {
        id: 'nav-rules',
        title: '분류 규칙',
        subtitle: '자동 분류 규칙 관리',
        icon: ListChecks,
        action: () => navigateTo('/rules'),
        keywords: ['rules', '규칙', '자동분류', 'auto'],
        category: 'navigation',
      },
      {
        id: 'nav-classification-status',
        title: '분류 현황',
        subtitle: '시스템 분류 상태',
        icon: PieChart,
        action: () => navigateTo('/classification-status'),
        keywords: ['classification', '분류현황', '상태', 'status'],
        category: 'navigation',
      },
      {
        id: 'nav-payment-methods',
        title: '결제수단',
        subtitle: '카드 및 계좌 관리',
        icon: CreditCard,
        action: () => navigateTo('/payment-methods'),
        keywords: ['payment', '결제', '카드', 'card', '계좌', 'account'],
        category: 'navigation',
      },
      {
        id: 'nav-notifications',
        title: '알림',
        subtitle: '예산 알림 확인',
        icon: Bell,
        action: () => navigateTo('/notifications'),
        keywords: ['notifications', '알림', 'alert'],
        category: 'navigation',
      },
      {
        id: 'nav-family',
        title: '가족 관리',
        subtitle: '가족 구성원 설정',
        icon: Users,
        action: () => navigateTo('/family'),
        keywords: ['family', '가족', '구성원', 'member'],
        category: 'navigation',
      },
      {
        id: 'nav-settings',
        title: '설정',
        subtitle: '앱 설정',
        icon: Settings,
        action: () => navigateTo('/settings'),
        keywords: ['settings', '설정', 'config'],
        category: 'navigation',
      },
      {
        id: 'nav-audit-log',
        title: '변경 이력',
        subtitle: '데이터 변경 기록',
        icon: History,
        action: () => navigateTo('/audit-log'),
        keywords: ['audit', '이력', 'history', 'log'],
        category: 'navigation',
      },
      {
        id: 'nav-help',
        title: '도움말',
        subtitle: '사용 가이드',
        icon: HelpCircle,
        action: () => navigateTo('/help'),
        keywords: ['help', '도움말', 'guide', '가이드'],
        category: 'navigation',
      },
      {
        id: 'nav-service-guide',
        title: '서비스 가이드',
        subtitle: '상세 사용 설명서',
        icon: BookOpen,
        action: () => navigateTo('/service-guide'),
        keywords: ['service', '서비스', 'guide', '가이드', 'manual'],
        category: 'navigation',
      },

      // Action commands
      {
        id: 'action-upload',
        title: '파일 업로드',
        subtitle: '새 거래 내역 가져오기',
        icon: Plus,
        action: () => navigateTo('/upload'),
        keywords: ['new', '새', 'upload', '업로드', 'add', '추가'],
        category: 'action',
      },
      {
        id: 'action-search-transactions',
        title: '거래 검색',
        subtitle: '거래 내역에서 검색',
        icon: Search,
        action: (query?: string) =>
          navigateTo(query?.trim() ? `/transactions?q=${encodeURIComponent(query.trim())}` : '/transactions'),
        keywords: ['search', '검색', 'find', '찾기', 'transaction', '거래'],
        category: 'action',
      },
    ];
  }, [navigate, close]);

  // Filter commands by query
  const filterCommands = useCallback(
    (query: string): CommandItem[] => {
      if (!query.trim()) return commands;

      const lowerQuery = query.toLowerCase();
      return commands.filter((cmd) => {
        return (
          cmd.title.toLowerCase().includes(lowerQuery) ||
          cmd.subtitle?.toLowerCase().includes(lowerQuery) ||
          cmd.keywords.some((kw) => kw.toLowerCase().includes(lowerQuery))
        );
      });
    },
    [commands]
  );

  return {
    isOpen,
    open,
    close,
    toggle,
    commands,
    filterCommands,
  };
}
